/**
 * SEO & GEO constants – single source of truth for meta and structured data
 */
export const SITE_URL = 'https://www.synaptek.fr';
export const SITE_NAME = 'Synaptek';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
export const TWITTER_HANDLE = '@synaptek_fr';

/** Default meta for homepage */
export const DEFAULT_META = {
  title: 'Synaptek — Conception d’orchestrateurs multi-agents | Annecy',
  description:
    "Synaptek conçoit des orchestrateurs multi-agents sur-mesure pour les entreprises d’Annecy, de Haute-Savoie et de l’axe Annecy–Genève : agents spécialisés, outils & SI, supervision humaine.",
  keywords:
    "orchestrateur multi-agents, conception d’orchestrateurs multi-agents, système multi-agents sur-mesure, orchestration d’agents IA, agents IA spécialisés, Annecy, Haute-Savoie, Alpes, Annecy-Genève, Synaptek",
  canonical: `${SITE_URL}/`,
  image: DEFAULT_OG_IMAGE,
  locale: 'fr_FR',
  type: 'website',
};

/** Geo targeting – Annecy, Haute-Savoie (local SEO) */
export const GEO = {
  region: 'FR-74',
  placename: 'Annecy-le-Vieux',
  position: '45.9197;6.1436',
  icbm: '45.9197, 6.1436',
  locality: 'Annecy-le-Vieux',
  country: 'FR',
};

/** JSON-LD structured data for homepage SEO */
export const HOME_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description: DEFAULT_META.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '19 Chemin de Charnay',
      addressLocality: 'Annecy-le-Vieux',
      postalCode: '74940',
      addressRegion: 'Haute-Savoie',
      addressCountry: 'FR',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@synaptek.fr',
      telephone: '+33-6-26-52-18-44',
      contactType: 'customer service',
      areaServed: 'FR',
      availableLanguage: ['French', 'English'],
    },
    sameAs: ['https://www.linkedin.com/company/synaptek-fr'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_META.description,
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'fr-FR',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    image: DEFAULT_OG_IMAGE,
    url: SITE_URL,
    telephone: '+33-6-26-52-18-44',
    email: 'hello@synaptek.fr',
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '19 Chemin de Charnay',
      addressLocality: 'Annecy-le-Vieux',
      postalCode: '74940',
      addressRegion: 'Haute-Savoie',
      addressCountry: 'FR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 45.9197,
      longitude: 6.1436,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  },
];

/**
 * Build a BreadcrumbList JSON-LD object for a page.
 * @param {Array<{name: string, path: string}>} items - ordered breadcrumb entries (Home implicite)
 */
export const buildBreadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Accueil',
      item: `${SITE_URL}/`,
    },
    ...items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 2,
      name: entry.name,
      item: entry.path?.startsWith('http')
        ? entry.path
        : `${SITE_URL}${entry.path?.startsWith('/') ? entry.path : `/${entry.path}`}`,
    })),
  ],
});

/**
 * Build a Service JSON-LD object for a solution/secteur page.
 * @param {{ name: string, description: string, path: string, serviceType?: string, areaServed?: string }} args
 */
export const buildServiceJsonLd = ({ name, description, path, serviceType, areaServed = 'FR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  serviceType: serviceType || name,
  url: path?.startsWith('http') ? path : `${SITE_URL}${path?.startsWith('/') ? path : `/${path}`}`,
  provider: { '@id': `${SITE_URL}/#organization` },
  areaServed,
  inLanguage: 'fr-FR',
});
